import React, { useEffect, forwardRef, useState, useRef } from "react";
import {
    Button,
    Carousel,
    Tooltip,
    Card,
    message,
    Image,
    Dropdown,
    Menu,
} from "antd";
import {
    ArrowLeftOutlined,
    ArrowRightOutlined,
    FullscreenOutlined,
    HeartOutlined,
    HeartFilled,
    EditOutlined,
    LinkOutlined,
    UnorderedListOutlined,
    RedoOutlined,
    DeleteOutlined,
    CheckCircleOutlined,
    LoadingOutlined,
    DownloadOutlined,
    InfoCircleOutlined,
    ForkOutlined
} from "@ant-design/icons";
import ImageZoomAdmin from "../../CustomerComponents/ImageZoomAdmin";
import "./TwoDImages.scss";
import HighResModal from "../AIEditorModalViewer/HighResModal.jsx";
import ShareModal from "../AIEditorModalViewer/ShareModal.jsx";
import CompareSiloView from "../AIEditorModalViewer/CompareSiloView.jsx";
import { downloadFile, copyToClipboard, downloadFileAsJpeg } from "../../Utilities";
import ImageZoom from "../AIEditorModalViewer/ImageZoom.jsx";
import GenerateAiVideoModal from "../GenerateAiVideoModal/GenerateAiVideoModal";
import TwoDImagesContext from "../../ContextFiles/TwoDImagesContext";
import CustomerProductContext from "../../ContextFiles/CustomerProductContext";
import { useContext } from "react";
import ReactPlayer from "react-player";
import pako from "pako";
import axios from "axios";
import ENVIRONMENT from "../../../../environments";
import { addDownloadIconObserver } from "../../../../utils/addImageDownload";

const image_style = { objectFit: "contain", width: "100%", height: "62vh" };

const formatDate = (dateString) => {
    if (!dateString) return '';
    
    let date;
    const strValue = String(dateString);

    if (/^\d+(\.\d+)?$/.test(strValue)) {
        // Parse as Unix timestamp in milliseconds
        date = new Date(parseFloat(strValue));
    } else {
        const utcDateString = strValue.endsWith('Z') ? strValue : strValue + 'Z';
        date = new Date(utcDateString);
    }
    
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
    });
};
let isAiCustomer = localStorage.getItem('is_ai_customer') === 'true';
const AIImageCarousel = forwardRef((props, ref) => {
    const {
      imageDate,
      createdBy,
      product_id,
      prompt_text,
      image_source,
      getFullResolution,
      imageFormat,
      prev,
      next,
      filteredImages,
      displayedImages,
      carouselIndex,
      from,
      generateFinalImage,
      generateFinalImageLoader,
      selectedImg,
      handleDeleteModal,
      handleMouseEnter,
      handleMouseLeave,
      handleKeyPressForRename,
      makeRenderNameEditable,
      renameRenderName,
      editRenderNameLoader,
      editedRenderUrl,
      tooltipVisible,
      selectedImageType,
      setEditedRenderUrl,
      filenameBeingEdited,
      renderDelete,
      handleFavorite,
      handleUnfavorite,
      handleSubmitForReview
    } = props;
    
    const [showHighResModal, setShowHighResModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [isGenerateVideoModalOpen, setIsGenerateVideoModalOpen] = useState(false);
    const [compareSiloModalOpen, setCompareSiloModalOpen] = useState(false);
    const [compareImageData, setCompareImageData] = useState(null);
    const twoDImagesContext = useContext(TwoDImagesContext);
    const productContext = useContext(CustomerProductContext);
    const { setImageType, refetch, handleCollaborateAiImage: contextHandleCollaborateAiImage } = twoDImagesContext || {};
    const { fetchAiAssets } = productContext || {};
    const renderNameRef = useRef(null);
    const imageZoomRef = useRef(null);

    useEffect(() => {
        if (imageZoomRef.current && image_source) {
            setTimeout(() => {
                if (imageZoomRef.current) {
                    imageZoomRef.current.resetTransform();
                }
            }, 100);
        }
    }, [image_source]);

    // Wrapper function to make render name editable using the ref
    const handleMakeRenderNameEditable = () => {
        if (renderNameRef.current) {
            renderNameRef.current.contentEditable = true;
            renderNameRef.current.focus();
            // Set cursor to end of text
            const range = document.createRange();
            const selection = window.getSelection();
            range.selectNodeContents(renderNameRef.current);
            range.collapse(false);
            selection.removeAllRanges();
            selection.addRange(range);
        }
        // Also call the parent function for state management
        if (makeRenderNameEditable) {
            makeRenderNameEditable();
        }
        setEditedRenderUrl(image_source);
    };

    // Update carousel when carouselIndex or image_source changes
    useEffect(() => {
        if (ref && ref.current && carouselIndex !== undefined && carouselIndex >= 0 && displayedImages && carouselIndex < displayedImages.length) {
            ref.current.goTo(carouselIndex, true);
        }
    }, [carouselIndex, image_source, displayedImages]);

    // Reset edit state when image changes
    useEffect(() => {
        if (renderNameRef.current && !filenameBeingEdited) {
            renderNameRef.current.contentEditable = false;
        }
    }, [image_source, filenameBeingEdited]);

    const goToAIEditor = () => {
        const params = new URLSearchParams({
            imageUrl: image_source,
            promptText: selectedImg.flux_prompt || selectedImg.prompt_text,
            productId: product_id || '',
            imageId: selectedImg.id || ''
        });
        window.location.href = '/edit-ai-image?' + params.toString();
    };

    const goToProductDetails = () => {
        window.location.href = '/products/' + product_id;
    };

    const goToGenerateMore = () => {
        const toBase64Url = (uint8) => {
            let binary = '';
            for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i]);
            return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
        };
        const compressState = (obj) => {
            const json = JSON.stringify(obj || {});
            const deflated = pako.deflate(json);
            return toBase64Url(deflated);
        };

        const params = new URLSearchParams();
        params.set('stage', 'lifestyle_generation');
        params.set('productId', product_id || '');
        if (selectedImg?.job_id) params.set('job_id', selectedImg.job_id);
        params.set('aiModel', 'true');

        const promptVal = (selectedImg?.prompt_text ?? prompt_text ?? '').toString();
        const state = {
            img: selectedImg.input_image_uri ? selectedImg.input_image_uri : (selectedImg.src || ''),
            imgInspection: selectedImg.inspiration_image_uri || '',
            inspiration_id: selectedImg.inspiration_id || null,
            prompt: promptVal,
            props: selectedImg.props || [],
            aspect_ratio: selectedImg.latest_aspect_ratio || "1:1",
        };
        params.set('state', compressState(state));

        window.location.href = `/ai-asset-creator?${params.toString()}`;
    };

    // Collaborate: use context handler when available (e.g. image gallery), else create collab and redirect (e.g. product page)
    const internalHandleCollaborateAiImage = async (obj) => {
        const toBase64Url = (uint8) => {
            let binary = '';
            for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i]);
            return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
        };
        const compressState = (stateObj) => {
            const json = JSON.stringify(stateObj || {});
            const deflated = pako.deflate(json);
            return toBase64Url(deflated);
        };
        const prompt_text = obj?.flux_prompt || obj?.prompt_text;
        const state = {
            img: obj?.input_image_uri || "",
            imgInspection: obj?.inspiration_image_uri || "",
            inspiration_id: obj?.inspiration_id || null,
            prompt: prompt_text || "",
            props: obj?.props || [],
        };
        const collaborateParams = new URLSearchParams();
        collaborateParams.set("ai_image", "true");
        collaborateParams.set("individual_collab", "true");
        collaborateParams.set("productId", obj?.entity_id || obj?.product_id || product_id || "");
        if (obj?.job_id) collaborateParams.set("job_id", String(obj.job_id));
        if (obj?.inspiration_id != null) collaborateParams.set("inspiration_id", String(obj.inspiration_id));
        collaborateParams.set("state", compressState(state));

        if (obj?.individual_collab_id) {
            window.location.href = `/collaborate-tool/${obj.individual_collab_id}?${collaborateParams.toString()}`;
            return;
        }
        message.loading('Collaborating on image...', 0);
        const userName = localStorage.getItem('username');
        let uri = obj?.uri || obj?.src || obj?.url || "";
        
        // Remove base URL if it exists
        const baseUrl = ENVIRONMENT.getBaseURL();
        if (uri.startsWith(baseUrl)) {
            uri = uri.substring(baseUrl.length);
        }
        
        const payload = {
            image_id: obj?.id,
            display_name: obj?.display_name || "Untitled",
            camera_name: uri,
            thumbnail: uri.replace(/(\.[^.]+)$/, "_512$1"),
            created_by: userName,
            last_modified_by: userName,
            render_path: uri,
            state: {},
        };
        try {
            const res = await axios.post(ENVIRONMENT.CREATE_COLLABORATE_ENTITY, payload);
            message.destroy();
            window.location.href = `/collaborate-tool/${res.data.body.collaboration_entity_id}?${collaborateParams.toString()}`;
            message.success('Collaboration created successfully');
        } catch (err) {
            message.destroy();
            message.error('Failed to start collaboration');
            console.error(err);
        }
    };

    const handleCollaborateClick = (e, image) => {
        e.stopPropagation();
        if (contextHandleCollaborateAiImage) {
            contextHandleCollaborateAiImage(image);
        } else {
            internalHandleCollaborateAiImage(image);
        }
    };

    // Build imageData shape for CompareSiloView (same as AIEditorModalViewer) so props and inspiration show as bottom cards
    const buildCompareImageData = (image) => {
        if (!image) return null;
        const selectedImageUrl = image?.url || image?.src;
        // Derive props_image_uris from props if not already set (e.g. from image gallery API)
        const props_image_uris = image?.props_image_uris ?? (image?.props?.map?.((p) => p?.image_uri ?? p?.uri).filter(Boolean)) ?? [];
        return {
            ...image,
            selectedImageUrl,
            product_id: image?.product_id || image?.entity_id || product_id,
            platform: image?.platform,
            inspiration_image_uri: image?.inspiration_image_uri ?? image?.inspiration_image,
            processed_silo: image?.processed_silo ?? image?.silo_uri,
            original_input_image_uri: image?.original_input_image_uri ?? image?.input_image_uri,
            props_image_uris: props_image_uris.length ? props_image_uris : image?.props_image_uris,
            render_props: image?.props
        };
    };

    const openCompareSilo = (e, image) => {
        e.stopPropagation();
        setCompareImageData(buildCompareImageData(image));
        setCompareSiloModalOpen(true);
    };

    const closeCompareSiloModal = () => {
        setCompareSiloModalOpen(false);
        setCompareImageData(null);
    };

    // const downloadImage = () => {
    //     let image_url = selectedImg.url || selectedImg.src
    //     if (!image_url) return;
        
    //     message.loading('Downloading image...', 0);
        
    //     fetch(image_url)
    //         .then(response => response.blob())
    //         .then(blob => {
    //             const url = window.URL.createObjectURL(blob);
    //             const link = document.createElement('a');
    //             link.href = url;
    //             link.download = `ai-image-${Date.now()}.png`;
    //             document.body.appendChild(link);
    //             link.click();
    //             document.body.removeChild(link);
    //             window.URL.revokeObjectURL(url);
                
    //             message.destroy();
    //             message.success('Image downloaded successfully!');
    //         })
    //         .catch(error => {
    //             console.error('Error downloading image:', error);
    //             message.destroy();
    //             message.error('Failed to download image. Please try again.');
    //         });
    // };

    const downloadImage = () => {
        let image_url = selectedImg.url || selectedImg.src
        if (!image_url) return;
        if (image_url) {
            let extension = image_url.split('.').pop();
            downloadFile(image_url, selectedImg.display_name, extension);
        }
    };

    const downloadImageAsJpeg = () => {
        const image_url = selectedImg.url || selectedImg.src;
        if (!image_url) return;
        downloadFileAsJpeg(image_url, selectedImg.display_name || 'image');
    };

    const isPngUrl = (url) => url && url.split('?')[0].toLowerCase().endsWith('.png');

    const getCompressedViewUrl = (url) => {
        if (!url) return url;
        return url.replace(/\.[^./?#]+(\?|$)/, '_compressed.webp$1');
    };

    const imageUrl = selectedImg?.url || selectedImg?.src;
    const pngDownloadMenu = (
        <Menu>
            <Menu.Item key="original" onClick={downloadImage}>
                <span className="manrope f-14">Download Original (PNG)</span>
            </Menu.Item>
            <Menu.Item key="jpeg" onClick={downloadImageAsJpeg}>
                <span className="manrope f-14">Download as JPEG</span>
            </Menu.Item>
        </Menu>
    );

    // Add download button inside Ant Design Image preview (fullscreen view)
    useEffect(() => {
        const observer = addDownloadIconObserver(downloadImage);
        return () => observer.disconnect();
    }, [selectedImg]);

    return (
        <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <div style={{ flex: 1 }}>
                <div className="manrope f-20 lh-28 w-500 black-085 justify-in-start" style={{ paddingLeft: '16px' }}>
                    <div className='ai-gradient-container'>
                        <img src="/img/ai_creator/ai-twinkle-gradient.png" />
                        <span className='manrope f-14 w-600 lh-28 ai-gradient ml-4'>AI</span>
                    </div>
                        <div className='justify-in-start'>
                            <Tooltip visible={tooltipVisible} title={<span className="manrope f-12 white">{selectedImg.display_name || 'Untitled'}</span>}>

                                <h4 ref={renderNameRef} id='render-name' onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onKeyDown={(e) => { e.stopPropagation(); handleKeyPressForRename(e.keyCode) }}
                                    className={`manrope f-20 clamp-text w-100 mb-0 scene-render-name${editedRenderUrl == image_source ? `-editable` : ``}`}>
                                    {selectedImg.display_name || 'Untitled'}</h4>
                            </Tooltip>

                            {selectedImageType == 'ai' || selectedImageType == 'ai_lifestyle' || selectedImageType == 'silo' || selectedImageType == 'lifestyle'  ?
                                filenameBeingEdited && editedRenderUrl == image_source ?
                                    <Button disabled={editRenderNameLoader} style={{ display: 'flex', justifyContent: 'flex-end' }} className="save-button font-12 ml-10" onClick={() => {
                                        renameRenderName()
                                    }}>
                                        Save {editRenderNameLoader ? <LoadingOutlined /> : ''}
                                    </Button>
                                    :
                                    <div onClick={handleMakeRenderNameEditable} className="edit-icon-data-small edit-icon-blue mt-0" >
                                        <EditOutlined className='edit-icon-property ml-10' />
                                    </div>
                                : ''}
                        </div>
                </div>
                <div className="justify-in-start mt-8" style={{ paddingLeft: '16px' }}>
                    <span className="manrope f-12 w-500 lh-20 black-00">
                        {getFullResolution} &nbsp;
                    </span>
                    <span className="manrope f-12 w-500 lh-20 black-085 upper-case" >
                        {imageFormat}&nbsp;&nbsp;
                    </span>
                    {imageDate && <span className="manrope f-12 w-500 lh-20 gray-045">
                        Created on {formatDate(imageDate)}&nbsp;
                    </span>}
                    {createdBy && <span className="manrope f-12 w-500 lh-20 gray-045">
                    by <b>{createdBy}</b>
                    </span>}
                </div>
            </div>
            {selectedImg?.image_type !== 'ai_video' && selectedImg?.main_type !== 'ai_video' && (
                <div 
                    className="manrope f-12 w-500 lh-20 gray-045 display-flex align-center pointer" 
                    style={{border: '1px solid #D9D9D9', borderRadius: '4px', padding: '6px 16px', cursor: 'pointer', height: 'fit-content'}}
                    onClick={() => {setIsGenerateVideoModalOpen(true); console.log('clicked', selectedImg)}}
                >
                    <img src="/img/video-camera-icon.svg" alt="video-camera" />
                    <span className="manrope f-16 black-085 ml-10">Generate Video</span>
                </div>
            )}
        </div>
            <div className="ai-final-image-container mt-24">
                <Button
                    className="carousel-btns left-btn for-ai"
                    onClick={prev}
                    style={{ marginLeft: 20 }}
                >
                    <ArrowLeftOutlined className="arrow" />
                </Button>
                <Carousel
                    autoplay={false}
                    ref={ref}
                    className="w-100 ai-image-carousel-container"
                    style={{ paddingBottom: '16px', paddingLeft: '16px' }}
                    initialSlide={carouselIndex}
                >
                    {displayedImages.map((image, index) => (
                        <Card
                            key={index}
                            className="lifestyle-carousel-card ai-image-carousel-card">
                            <div className='ai-image-btns-container'>
                                <div className='justify-in-start'>
                                    
                                    {image.image_type !== 'ai_video' && image.main_type !== 'ai_video' &&
                                        <Tooltip title={<span className="manrope f-12 white">Collaborate</span>}>
                                            <Button className='manrope f-14 w-500 lh-22 ai-img-btns justify-in-center'
                                                onClick={(e) => handleCollaborateClick(e, image)}
                                            >
                                                <img src={'/img/collaborate_tool/new-new-collab-1.2.svg'} width={16} height={18} />
                                                <span className='ml-8 manrope f-14 w-500'>Collaborate</span>
                                            </Button>
                                        </Tooltip>}
                                    {/* Hide Edit button for videos */}
                                    {image.image_type !== 'ai_video' && image.main_type !== 'ai_video' && (
                                        <Tooltip title={<span className="manrope f-12 white">Edit Image</span>}>
                                            <Button className='manrope f-14 w-500 lh-22 ai-img-btns justify-in-center ml-8' onClick={goToAIEditor}>
                                                <EditOutlined />Edit
                                            </Button>
                                        </Tooltip>
                                    )}
                                    {image.image_type !== 'ai_video' && image.main_type !== 'ai_video' && (
                                        <Tooltip title={<span className="manrope f-12 white">Compare with silo</span>}>
                                            <Button className='manrope f-14 w-500 lh-22 ai-img-btns ml-8 justify-in-center'
                                                onClick={(e) => openCompareSilo(e, image)}
                                            >
                                                <ForkOutlined />Compare
                                            </Button>
                                        </Tooltip>)}
                                </div>
                                <div className='justify-in-end'>
                                    {   selectedImg.isFavourite? 
                                        <Tooltip title={<span className="manrope f-12 white">Unfavorite</span>}>
                                            <Button className='manrope f-14 w-500 lh-22 ai-heart-btn' onClick={() => handleUnfavorite(selectedImg)}>
                                                <HeartFilled />
                                            </Button>
                                        </Tooltip>
                                        :
                                        <Tooltip title={<span className="manrope f-12 white">Favorite</span>}>
                                            <Button className='manrope f-14 w-500 lh-22 ai-heart-btn' onClick={() => handleFavorite(selectedImg)}>
                                                <HeartOutlined />
                                            </Button>
                                        </Tooltip>}
                                    {/* Hide Expand button for videos */}
                                    {image.image_type !== 'ai_video' && image.main_type !== 'ai_video' && (
                                        <Tooltip title={<span className="manrope f-12 white">Expand Image</span>}>
                                            <Button 
                                                className='ai-fullscreen-btn ml-8'
                                                onClick={() => setPreviewVisible(true)}
                                            >
                                                <FullscreenOutlined />
                                            </Button>
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                            {carouselIndex === index ? (
                                <>
                                    {image.image_type === 'ai_video' || image.main_type === 'ai_video' ? (
                                        // Video rendering
                                        image.status === 'inprogress' ? (
                                            <div className="ai-video-inprogress-container">
                                                <img 
                                                    src={image.thumbnail || image.low_url} 
                                                    alt="Video thumbnail"
                                                    className="ai-video-inprogress-thumbnail"
                                                />
                                                <div className="ai-video-inprogress-overlay">
                                                    Video in Progress
                                                </div>
                                            </div>
                                        ) : (
                                            <ReactPlayer
                                                url={image.src || image.url}
                                                controls
                                                width="100%"
                                                height="62vh"
                                                style={{ borderRadius: '4px' }}
                                            />
                                        )
                                    ) : (
                                        // Image rendering
                                        <ImageZoom
                                            ref={imageZoomRef}
                                            imageSrc={getCompressedViewUrl(image.src || image.url)}
                                            fallbackSrc={image.src || image.url}
                                            className="ai-image-carousel"
                                            showZoomControls={true}
                                        />
                                    )}
                                </>
                            ) : (
                                ""
                            )}

                            <div className="carousel-index-display">
                                <span className="manrope f-14 w-500">
                                    {index + 1} of {filteredImages?.length}
                                </span>
                            </div>
                        </Card>
                    ))}
                </Carousel>
                <Button className="carousel-btns right-btn for-ai" onClick={next}>
                    <ArrowRightOutlined className="arrow" />
                </Button>

                <div className="border-top"></div>

                <div className='ai-image-buttons' style={{ paddingRight: '0px', paddingBottom: '10px', paddingLeft: '16px' }}>
                    <div className='justify-in-start' style={{ display: 'flex', gap: '8px' }}>
                        <Tooltip title={<span className="manrope f-12 white">Submit for review</span>}>
                            <Button 
                                className='manrope f-14 w-500 lh-22 ai-action-btn'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSubmitForReview(selectedImg);
                                }}
                            > 
                                <img src={'/img/ai_creator/submit_for_review-black.svg'} width={16} height={18} /> 
                            </Button>
                        </Tooltip>
                        {/* Hide download, share, delete for in-progress videos */}
                        {!(selectedImg?.status === 'inprogress' && (selectedImg?.image_type === 'ai_video' || selectedImg?.main_type === 'ai_video')) && (
                            <>
                                {(selectedImg?.image_type === 'ai_video' || selectedImg?.main_type === 'ai_video') || !isPngUrl(imageUrl) ? (
                                    <Tooltip title={<span className="manrope f-12 white">
                                        {(selectedImg?.image_type === 'ai_video' || selectedImg?.main_type === 'ai_video') ? "Download Video" :
                                         selectedImg?.is_final_image ? "Download High-Res Image" : "Download Preview"}
                                    </span>}>
                                        <Button className='manrope f-14 w-500 lh-22 ai-action-btn' icon={<DownloadOutlined />} onClick={() => {
                                            if (selectedImg?.image_type === 'ai_video' || selectedImg?.main_type === 'ai_video') {
                                                downloadFile(selectedImg.url || selectedImg.src, 'Untitled.mp4');
                                            } else {
                                                downloadImage();
                                            }
                                        }} />
                                    </Tooltip>
                                ) : (
                                    <Dropdown overlay={pngDownloadMenu} trigger={['click']}>
                                        <Tooltip title={<span className="manrope f-12 white">{selectedImg?.is_final_image ? "Download High-Res Image" : "Download Preview"}</span>}>
                                            <Button className='manrope f-14 w-500 lh-22 ai-action-btn' icon={<DownloadOutlined />} />
                                        </Tooltip>
                                    </Dropdown>
                                )}

                                <Tooltip title={<span className="manrope f-12 white">Share</span>}>
                                    <Button
                                        className='manrope f-14 w-500 lh-22 ai-action-btn'
                                        icon={<LinkOutlined />}
                                        onClick={() => setShowShareModal(true)}
                                    />
                                </Tooltip>
                            </>
                        )}

                        {from != 'product_page' && (
                        <Tooltip title={<span className="manrope f-12 white">Product Details</span>}>
                            <Button onClick={goToProductDetails} className='manrope f-14 w-500 lh-22 ai-action-btn' icon={<UnorderedListOutlined />} />
                        </Tooltip>
                        )}

                        {/* Hide Generate More button for videos */}
                        {selectedImg?.image_type !== 'ai_video' && selectedImg?.main_type !== 'ai_video' && (
                            <Tooltip title={<span className="manrope f-12 white">Generate More</span>}>
                                <Button className='manrope f-14 w-500 lh-22 ai-action-btn' icon={<RedoOutlined />} onClick={goToGenerateMore}/>
                            </Tooltip>
                        )}

                        {/* Hide delete for in-progress videos */}
                        {!((selectedImg?.image_type === 'ai_video' || selectedImg?.main_type === 'ai_video') && selectedImg?.status === 'inprogress') && (
                            renderDelete ? renderDelete(selectedImg) :
                            <Tooltip title={<span className="manrope f-12 white">Delete</span>}>
                                <Button className='manrope f-14 w-500 lh-22 ai-action-btn-red' icon={<DeleteOutlined />} onClick={() => handleDeleteModal(selectedImg)} />
                            </Tooltip>
                        )}
                    </div>

                    {/* Right side buttons */}
                    <div className='justify-in-end' style={{ display: 'flex', gap: '8px' }}>
                        {/* For completed videos - show Download Video button */}
                        {(selectedImg?.image_type === 'ai_video' || selectedImg?.main_type === 'ai_video') && selectedImg?.status !== 'inprogress' && (
                            <>
                            <div className='manrope f-14 w-700 lh-22 green-73'>
                                <CheckCircleOutlined className='manrope f-14 green-73' />
                                <span className='ml-6'>Purchased</span>
                                <span className='manrope f-14 w-500 lh-22 black-085 ml-8'>No additional credits needed</span>
                            </div>
                            <Button
                                className='manrope f-14 w-500 lh-22 ai-action-btn-blue'
                                onClick={() => downloadFile(selectedImg.url || selectedImg.src, 'Untitled.mp4')}
                            >
                                Download Video
                            </Button>
                            </>
                        )}

                        {/* For images - show final image buttons */}
                        {selectedImg?.image_type !== 'ai_video' && selectedImg?.main_type !== 'ai_video' && (
                            <>
                                {selectedImg?.is_final_image ?
                                <div className='manrope f-14 w-700 lh-22 green-73'>
                                    <CheckCircleOutlined className='manrope f-14 green-73' />
                                    <span className='ml-6'>Purchased</span>
                                    <span className='manrope f-14 w-500 lh-22 black-085 ml-8'>No additional credits needed</span>
                                </div> : 
                                ""}
                                {
                                    selectedImg?.is_final_image ?
                                    isPngUrl(imageUrl) ? (
                                        <Dropdown overlay={pngDownloadMenu} trigger={['click']}>
                                            <Button className='manrope f-14 w-500 lh-22 ai-action-btn-blue'>
                                                Download High-Res Image
                                            </Button>
                                        </Dropdown>
                                    ) : (
                                        <Button
                                            className='manrope f-14 w-500 lh-22 ai-action-btn-blue'
                                            onClick={downloadImage}
                                        >
                                            Download High-Res Image
                                        </Button>
                                    )
                                    :
                                    selectedImg?.type == 'ai_lifestyle' || selectedImg?.type == 'ai' ?
                                    <Button
                                        className='manrope f-14 w-500 lh-22 ai-action-btn-blue'
                                        onClick={() => setShowHighResModal(true)}
                                        ghost
                                        >
                                        Save As Final
                                    </Button>
                                    : null
                                }
                            </>
                        )}
                    </div>
                </div>


            </div>
            <HighResModal
                isOpen={showHighResModal}
                onClose={() => setShowHighResModal(false)}
                imageData={selectedImg}
            />

            <ShareModal
                isOpen={showShareModal}
                onClose={() => setShowShareModal(false)}
                imageData={{
                    ...selectedImg,
                    selectedImageUrl: selectedImg?.url || selectedImg?.src,
                    display_name: selectedImg?.display_name || selectedImg?.camera_display_name || 'Untitled',
                }}
            />
            
            <GenerateAiVideoModal
                visible={isGenerateVideoModalOpen}
                onCancel={() => setIsGenerateVideoModalOpen(false)}
                imageUrl={selectedImg?.thumbnail || selectedImg?.low_url}
                image_id={selectedImg?.id}
                onSuccess={async () => {
                    if (from === 'product_page' && fetchAiAssets) {
                        await fetchAiAssets();
                    } else if (refetch) {
                        await refetch();
                    }
                }}
            />

            <CompareSiloView
                ai_page={from === 'product_page'}
                isOpen={compareSiloModalOpen}
                onClose={closeCompareSiloModal}
                closeAllModals={closeCompareSiloModal}
                imageData={compareImageData}
            />

            <Image
                key={selectedImg?.id}
                style={{ display: 'none' }}
                src={getCompressedViewUrl(image_source)}
                fallback={image_source}
                preview={{
                    visible: previewVisible,
                    onVisibleChange: (visible) => setPreviewVisible(visible),
                }}
            />
        </>
    );
});

export default AIImageCarousel;
